/**
 *
 * ParentCommunicationListItem
 *
 */

import React, { memo, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import moment from 'moment';

// antd component
import { List } from 'antd';

// components
import ThumbnailUpload from 'components/ThumbnailUpload';

// icons
import { InfoCircleOutlined, RightOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import CustomIcons from 'components/CustomIcons';

import {
  getCommunicationType,
  getCommunicationTypeLabel,
  getCommunicationAudienceAsString,
  getResponseNeedAnswerTypes,
} from 'utils/helpers/helper.generateData';
import { isDateBeforeDateBySeconds } from 'utils/moment/moment.utils';

// global user
import {
  useGetLocalUser,
  useGetLocalActiveChild
} from 'engine/Contexts/User.context';

function ParentCommunicationListItem({
  element,
  searchString,
  // formats
  timeFormat,
  dateFormat,
  dateTimeFormat,
  // func
  customHighlighter,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames(
    'communication-list-meta', 
    className
    );

  const localUser = useGetLocalUser()();
  const localActiveChild = useGetLocalActiveChild()();

  const senderRole = element?.sender?.[0]?.role || 'admin';

  const date = `${(element?.filterDate ? moment(element?.filterDate).format(dateTimeFormat) : moment(element?.creationDate).format(dateTimeFormat))}`;
  const deadlineDate = `${element?.deadlineDate ? moment(element?.deadlineDate).format(dateFormat) : ''}`;

  const audienceStrFull = getCommunicationAudienceAsString(element);

  const isShowFormMark = () => {
    const key = "forms";
    const need = element?.needAnswers?.length == 0 && getResponseNeedAnswerTypes(element?.types, element?.answer);
    const needAnswer = element?.needAnswers?.length > 0 && element?.needAnswers || need?.needAnswers;
    const needForm = needAnswer?.includes(key);
    const hasType = element?.types?.filter(el => el == key);
    const hasForm = hasType?.length > 0;
    
    return (!!hasForm && !!needForm)
  }
  const isShowAppointmentMark = () => {
    const key = "appointments";
    const need = element?.needAnswers?.length == 0 && getResponseNeedAnswerTypes(element?.types, element?.answer);
    const needAnswer = element?.needAnswers?.length > 0 && element?.needAnswers || need?.needAnswers;
    const needAppointment = needAnswer?.includes(key);
    const hasType = element?.types?.filter(el => el == key);
    const hasAppointment = hasType?.length > 0;

    const adminAnserIsActual = isDateBeforeDateBySeconds(element?.adminAnswer?.adminAnswerDate, element?.adminAnswerReadDate);
    const newAdminAnswerReadDate = !!adminAnserIsActual || !!element?.adminAnswer && !!!element?.adminAnswerReadDate;

    return (
      (!!hasAppointment && !!needAppointment) ||
      !needAppointment && !!newAdminAnswerReadDate
    )
  }

  const isShowChat = () => {
    const key = "discussion";
    const hasType = element?.types?.filter(el => el == key);
    const hasDiscussion = hasType?.length > 0;
    const hasAsReaders = element?.readers?.filter(el => (
      (el?.user == localUser?._id)
    )) || [];
    const readers = element?.readersFiltered?.filter(el => (
      (el?.reader?.student == localActiveChild?._id) &&
      (el?.reader?.user == localUser?._id)
    ));
    const hasNewMessages = readers?.some(el => !!el?.hasNewMessages) || hasAsReaders?.length == 0;
    return !!hasDiscussion && !!hasNewMessages;
  }

  
  return element && (
    <List.Item.Meta
      className={childClassNames}
      title={false}
      description={
        <>
          <div className="schl-item-content">

            {element?.thumbnail && (
              <div className="schl-item-content__thumbnail">
                <ThumbnailUpload
                  onlyRead={true}
                  value={element?.thumbnail}
                  size="small"
                />
              </div>
            )}

            <div className="schl-item-content__context">

              <div className='sub-title'>
                <span className="date">
                  {customHighlighter(`${date}`, searchString)}
                </span>
                {!!deadlineDate && (
                    <>
                      <span className="date deadline">
                        {customHighlighter(`Date butoir: ${deadlineDate}`, searchString)}
                      </span> 
                    </>
                )}
              </div>
              <span className="title">{customHighlighter(element?.title, searchString)}</span>
              {element?.audience && (
                <span className='audience'>{customHighlighter(audienceStrFull, searchString)}</span>
              )}
              {element?.reason && (
                <span className='reason'>{customHighlighter(element?.reason, searchString)}</span>
              )}
              
            </div>

          </div>

          <div className="bottom">
            {element?.types && (
              <div className='tag-types'>
                {getCommunicationType(element)?.map((typeItem, i) => typeItem !== 'no-type' && (
                  <span key={typeItem + '-type-' + i} className={`tag-type tag-type--${typeItem}`}>{customHighlighter(getCommunicationTypeLabel(typeItem)?.label, searchString)}</span>
                ))}
              </div>
            )}
          </div>
          <div className="marks">
            {isShowAppointmentMark() && (
              <span className="mark appointment-mark"><CustomIcons type="calendar" /></span>
            )}
            {isShowFormMark() && (
              <span className="mark form-mark"><CustomIcons type="document" /></span>
            )}
            {isShowChat() && (
              <span className="mark discussion-mark"><CustomIcons type="comments" /></span>
            )}
          </div>
        </>
      }
    />
  );

};

ParentCommunicationListItem.defaultProps = {
  dateFormat: 'DD.MM.YYYY',
  timeFormat: 'HH:mm',
  dateTimeFormat: 'DD.MM.YYYY - HH:mm',
};
ParentCommunicationListItem.propTypes = {
  element: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.bool.isRequired,
  ]),
  searchString: PropTypes.string,
  customHighlighter: PropTypes.func,
};

export default memo(ParentCommunicationListItem);